import { useEffect } from "react";
import { HEIGHTS, MAX_FORECAST_TIME, getApiBaseUrl } from "../config/siteConfig";

/**
 * Hook to fetch data for a single site when switching to it before it's loaded
 * @param {number} siteIndex - Index of the site to fetch
 * @param {number} height - Current height setting
 * @param {object} cachedData - Currently cached data
 * @param {function} setCachedData - Function to update cached data
 * @param {function} setPositions - Function to set positions
 * @param {function} setDirections - Function to set directions
 */
function useFetchSingleSiteData(siteIndex, height, time, cachedData, setCachedData, setPositions, setDirections) {
    useEffect(() => {
        // Skip if this site data is already fully loaded
        if (cachedData && 
            cachedData[siteIndex] && 
            cachedData[siteIndex].positions && 
            cachedData[siteIndex][height] && 
            cachedData[siteIndex][height][time]) {
            console.log(`Site ${siteIndex} data already loaded`);
            return;
        }

        const fetchSiteData = async () => {
            const apiBaseUrl = getApiBaseUrl();
            let updatedData = { ...cachedData };
            
            if (!updatedData[siteIndex]) {
                updatedData[siteIndex] = {};
            }

            // First, fetch positions if not cached
            if (!updatedData[siteIndex].positions) {
                try {
                    console.log(`Prioritized loading: Fetching positions for site ${siteIndex}`);
                    const url = `${apiBaseUrl}/positions/${siteIndex}`;
                    
                    const positionsResponse = await fetch(url, {
                        cache: "no-store"
                    });
                    if (!positionsResponse.ok) throw new Error(`HTTP error! status: ${positionsResponse.status}`);
                    
                    const positionsData = await positionsResponse.json();
                    console.log(`Positions count for site ${siteIndex}: ${positionsData.positions.length}`);
                    
                    updatedData[siteIndex].positions = positionsData.positions;
                    setPositions(positionsData.positions);
                    
                    // Update the cache immediately after fetching positions
                    setCachedData(updatedData);
                } catch (error) {
                    console.error(`Error fetching positions for site ${siteIndex}:`, error);
                }
            }

            // Then fetch the current height and time data if not cached
            if (!updatedData[siteIndex][height] || !updatedData[siteIndex][height][time]) {
                try {
                    console.log(`Prioritized loading: Fetching data for site ${siteIndex}, height ${height}, time ${time}`);
                    
                    // Ensure the height object exists
                    if (!updatedData[siteIndex][height]) {
                        updatedData[siteIndex][height] = {};
                    }
                    
                    const timeUrl = `${apiBaseUrl}/data/${siteIndex}/${height}/${time}`;
                    const timeResponse = await fetch(timeUrl, {
                        cache: "no-store"
                    });
                    
                    if (!timeResponse.ok) throw new Error(`HTTP error! status: ${timeResponse.status}`);
                    
                    const data = await timeResponse.json();
                    updatedData[siteIndex][height][time] = { directions: data.directions };
                    
                    // Update directions in the UI
                    setDirections(data.directions);
                    
                    // Update the cache again
                    setCachedData(updatedData);
                    
                    console.log(`Prioritized loading complete for site ${siteIndex}, height ${height}, time ${time}`);
                } catch (error) {
                    console.error(`Error fetching data for site ${siteIndex}, height ${height}, time ${time}:`, error);
                }
            }
        };

        fetchSiteData();
    }, [siteIndex, height, time, cachedData, setCachedData, setPositions, setDirections]);
}

export default useFetchSingleSiteData; 